import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import TankIcon from "@/components/TankIcon";
import heroBackground from "@/assets/hero-background.jpg";
import { 
  Factory, 
  Shield, 
  CheckCircle, 
  ArrowRight, 
  Zap, 
  Settings, 
  Award,
  Users,
  Building,
  FlaskConical,
  Coffee,
  Pill
} from "lucide-react";

const Home = () => {
  const features = [
    {
      icon: <Factory className="h-8 w-8 text-wle-blue" />,
      title: "Custom Tank Fabrication",
      description: "From concept to commissioning, we deliver bespoke stainless steel tanks tailored to your exact specifications and industry requirements."
    },
    {
      icon: <Shield className="h-8 w-8 text-wle-red" />,
      title: "Premium Materials",
      description: "We exclusively use high-grade SS316L and specialty alloys, ensuring superior corrosion resistance and longevity for your investments."
    },
    {
      icon: <Settings className="h-8 w-8 text-wle-blue" />,
      title: "Complete EPCC Solutions",
      description: "Engineering, Procurement, Construction, and Commissioning services under one roof, streamlining your project delivery and accountability."
    },
    {
      icon: <Award className="h-8 w-8 text-wle-red" />,
      title: "Certified Quality Standards",
      description: "All fabrication meets JKKP standards and international codes, with comprehensive documentation and quality assurance protocols."
    }
  ];

  const industries = [
    { icon: <Pill className="h-6 w-6" />, name: "Pharmaceuticals", color: "bg-wle-blue", desc: "GMP-compliant tanks for sterile production" },
    { icon: <Coffee className="h-6 w-6" />, name: "Food & Beverage", color: "bg-wle-red", desc: "Hygienic solutions for food processing" },
    { icon: <FlaskConical className="h-6 w-6" />, name: "Chemical Processing", color: "bg-wle-blue", desc: "Corrosion-resistant vessels for chemicals" },
    { icon: <Building className="h-6 w-6" />, name: "Oil & Gas", color: "bg-wle-red", desc: "High-pressure storage and processing tanks" },
  ];

  const clients = [
    "Pharmaniaga Sdn Bhd",
    "Xepa-Soul Pattison Sdn Bhd", 
    "Majubina Bhd",
    "CCM Pharmaceutical Division",
    "Prime Pharma",
    "Leading F&B Companies",
    "Major Chemical Plants"
  ];

  return (
    <div className="min-h-screen bg-page-modern">
      <Helmet>
        <title>WLE Technology | Tank Fabrication & Electropolishing Malaysia</title>
        <meta name="description" content="Malaysia's premier tank fabrication and electropolishing specialist. Custom stainless steel tanks, mechanical polishing, and EPCC solutions for pharmaceutical, F&B, and chemical industries." />
        <link rel="canonical" href="https://wletec.com/" />
        <meta property="og:title" content="WLE Technology - Tank Fabrication Malaysia" />
        <meta property="og:description" content="Custom tank fabrication and surface finishing solutions for industries across Malaysia and Southeast Asia." />
        <meta property="og:url" content="https://wletec.com/" />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="tank fabrication Malaysia, electropolishing, stainless steel tanks, EPCC, mechanical polishing, pharmaceutical tanks" />
      </Helmet>
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" style={{ backgroundImage: `url(${heroBackground})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-background/60 backdrop-blur-[1px]"></div>
        {/* Floating Shapes */}
        <div className="floating-shapes">
          <div className="floating-shape"></div>
          <div className="floating-shape"></div>
          <div className="floating-shape"></div>
        </div>
        
        {/* Industrial Grid Overlay */}
        <div className="absolute inset-0 industrial-grid opacity-30"></div>
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Badge variant="outline" className="w-fit">
                Malaysia Mechanical and System Solutions
              </Badge>
              
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                Malaysia{" "}
                <span className="bg-gradient-primary bg-clip-text text-transparent">
                  Mechanical and System Solutions
                </span>
              </h1>
              
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="secondary" className="text-white">Mechanical Solutions</Badge>
                <Badge variant="secondary" className="text-white">System Integration</Badge>
                <Badge variant="secondary" className="text-white">EPCC Specialist</Badge>
              </div>
              
              <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
                Malaysia's premier mechanical & system solutions provider. We deliver complete integrated solutions including custom tank fabrication, precision mechanical polishing, advanced electropolishing, and comprehensive EPCC turnkey projects from engineering to commissioning.
              </p>

              <div className="bg-card/80 backdrop-blur-sm p-6 rounded-xl border shadow-blue">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-wle-red mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-sm font-semibold mb-2 text-wle-red">Beyond Fabrication</p>
                    <p className="text-foreground leading-relaxed">
                      Our comprehensive EPCC approach ensures seamless project delivery from initial design through final commissioning, backed by decades of engineering expertise.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="shadow-red hover:shadow-lg transition-all duration-300">
                  <Link to="/contact">Get Quote</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="hover:bg-wle-blue/10 hover:border-wle-blue transition-all duration-300">
                  <Link to="/services">Explore Services</Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="bg-card/90 backdrop-blur-sm p-6 rounded-xl border shadow-blue hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-wle-red/10 rounded-lg group-hover:bg-wle-red/20 transition-colors">
                        <TankIcon type="heated" size="lg" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">Heated Tanks</h3>
                        <p className="text-sm text-muted-foreground">Advanced thermal solutions for temperature-critical processes</p>
                        <p className="text-xs text-wle-red font-medium mt-1">Steam/Electric Heating</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-card/90 backdrop-blur-sm p-6 rounded-xl border shadow-red hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-wle-blue/10 rounded-lg group-hover:bg-wle-blue/20 transition-colors">
                        <TankIcon type="general" size="lg" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">Storage Tanks</h3>
                        <p className="text-sm text-muted-foreground">Robust storage solutions for liquids and bulk materials</p>
                        <p className="text-xs text-wle-blue font-medium mt-1">Various Configurations</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="md:mt-8">
                  <div className="bg-gradient-primary p-8 rounded-xl text-white relative overflow-hidden group hover:scale-105 transition-transform duration-300">
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative z-10">
                      <h3 className="font-bold text-2xl mb-2">Electropolishing</h3>
                      <p className="text-lg mb-2 font-semibold">Up to 6m Length</p>
                      <p className="text-sm mb-4 opacity-90">Malaysia's Largest Facility</p>
                      <div className="space-y-1 text-sm opacity-90">
                        <p className="font-medium">6000mm x 1500mm x 1500mm</p>
                        <p>Specialized Jigs & Fixtures</p>
                        <p>Mirror-Bright Ra &lt; 0.4μm Finish</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Animated piping */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                <div className="w-32 h-0.5 bg-wle-blue opacity-30 animate-pulse"></div>
                <div className="w-0.5 h-32 bg-wle-blue opacity-30 animate-pulse absolute top-0 left-1/2 transform -translate-x-1/2"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-20 bg-grid-pattern">
        <div className="absolute inset-0 bg-mesh-gradient"></div>
        <div className="container relative z-10">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">
              Why Choose WLE
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Engineering <span className="text-wle-red">Excellence</span> In Every Project
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Four decades of proven expertise in delivering mission-critical tank solutions that exceed industry standards and client expectations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-elegant hover:shadow-blue transition-all duration-300 group bg-card/50 backdrop-blur-sm">
                <CardContent className="p-8 text-center h-full flex flex-col">
                  <div className="mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-3 text-foreground">{feature.title}</h3>
                  <p className="text-sm text-foreground/70 leading-relaxed flex-1">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="relative py-20 bg-animated-gradient">
        <div className="absolute inset-0 bg-dots-pattern opacity-20"></div>
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold">
                Malaysia's Most Trusted Tank Fabrication Partner
              </h2>
              <p className="text-lg text-foreground/80">
                With over 40 years of engineering excellence, WLE Group has established itself as Malaysia's premier destination for specialized tank fabrication and finishing services.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-wle-blue" />
                  <span>Complete in-house finishing capabilities including Malaysia's largest electropolishing facility</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-wle-red" />
                  <span>Serving pharmaceutical, F&B, chemical, and oil & gas industries nationwide</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-wle-blue" />
                  <span>End-to-end EPCC solutions from design through commissioning</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-wle-red" />
                  <span>Custom engineering solutions tailored to your unique requirements</span>
                </div>
              </div>

              <Button asChild className="shadow-blue">
                <Link to="/about">
                  Learn More About WLE
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {industries.map((industry, index) => (
                <Card key={index} className="border-0 shadow-elegant hover:shadow-blue transition-all duration-300 group bg-card/70 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 rounded-full ${industry.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <div className="text-white">
                        {industry.icon}
                      </div>
                    </div>
                    <h3 className="font-semibold mb-2">{industry.name}</h3>
                    <p className="text-xs text-foreground/70">{industry.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-20 bg-subtle-texture">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Trusted By Industry Leaders</h2>
            <p className="text-lg text-foreground/80">
              Leading companies across Malaysia trust WLE for their most critical tank fabrication and finishing requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {clients.map((client, index) => (
              <Card key={index} className="border-0 shadow-elegant hover:shadow-blue transition-all duration-300 group bg-card/60 backdrop-blur-sm">
                <CardContent className="p-6 text-center group-hover:bg-wle-blue/5 transition-colors">
                  <Users className="h-6 w-6 text-wle-blue mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="font-medium text-sm leading-relaxed">{client}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-foreground text-background">
        <div className="container text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Ready to Start Your Custom Tank Project?
          </h2>
          <p className="text-lg mb-8 text-background/80 max-w-2xl mx-auto">
            Contact our engineering team today to discuss your requirements. We provide comprehensive project consultation and detailed quotations at no cost.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="outline">
              <Link to="/contact">Get Free Quote</Link>
            </Button>
            <Button asChild size="lg" variant="ghost" className="text-background hover:bg-background/10">
              <a href="https://wa.me/601139069019" target="_blank" rel="noopener noreferrer">
                WhatsApp Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;